import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LocuLogo from '../components/LocuLogo';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { initMSG91Widget, sendOTP } from '../utils/msg91Widget';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const IS_DEV = (process.env.REACT_APP_ENV || 'production') === 'dev';

const Login = () => {
  console.log('🔵 Login component rendering');
  const { isAuthenticated, setToken, setUser } = useAuth();
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [widgetInitialized, setWidgetInitialized] = useState(false);
  const [useEmailLogin, setUseEmailLogin] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isMerchant = location.state?.isMerchant || false;

  // Redirect authenticated users away from login page
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // loadMessage91Widget();
  }, []);

  // Store merchant intent for navbar detection
  useEffect(() => {
    if (isMerchant) {
      localStorage.setItem('merchantIntent', 'true');
      sessionStorage.setItem('isMerchant', 'true');
      setUseEmailLogin(true); // Merchants use email login by default
    } else {
      // Clear merchant intent if explicitly set to false
      if (location.state?.isMerchant === false) {
        localStorage.removeItem('merchantIntent');
        sessionStorage.removeItem('isMerchant');
      }
    }
  }, [isMerchant, location.state]);

  // Initialize MSG91 widget once on component mount
  // useEffect(() => {
  //   const initializeWidget = async () => {
  //     try {
  //       console.log('🔧 Initializing MSG91 widget on component mount...');
  //       await initMSG91Widget({
  //         phoneNumber: '', // Initialize without phone, will set it when sending OTP
  //         onSuccess: (data) => {
  //           console.log('✅ MSG91 Widget initialized successfully:', data);
  //           setWidgetInitialized(true);
  //         },
  //         onFailure: (error) => {
  //           console.error('❌ MSG91 Widget initialization failed:', error);
  //           setError('Failed to initialize OTP service. Please refresh the page.');
  //         }
  //       });
  //     } catch (error) {
  //       console.error('❌ Error initializing MSG91 widget:', error);
  //       setError('Failed to initialize OTP service. Please refresh the page.');
  //     }
  //   };

  //   initializeWidget();
  // }, []); // Run once on mount

  const validatePhone = (phoneNumber) => {
    const cleaned = phoneNumber.replace(/\s/g, '');
    return /^[6-9]\d{9}$/.test(cleaned);
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password
      });

      if (response.data.token) {
        // Use AuthContext methods to set token and user
        setToken(response.data.token);
        setUser(response.data.user);

        // Clear merchant intent if logged in as regular user
        if (response.data.user.role !== 'merchant') {
          localStorage.removeItem('merchantIntent');
          localStorage.removeItem('locuUserType'); // Clear merchant view flag
          sessionStorage.removeItem('isMerchant');
        } else {
          // Set merchant view for restaurant users
          localStorage.setItem('locuUserType', 'merchants');
        }

        // Redirect based on role
        if (response.data.user.role === 'merchant') {
          navigate('/merchant/dashboard');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('❌ Email login error:', error);
      setError(error.response?.data?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate phone number
    if (!phone || phone.length !== 10) {
      console.log('❌ Phone validation failed: length check');
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    if (!validatePhone(phone)) {
      console.log('❌ Phone validation failed: format check');
      setError('Please enter a valid Indian mobile number');
      return;
    }

    console.log('✅ Phone validation passed, setting loading to true');
    setLoading(true);

    try {
      console.log('🔵 Starting OTP send process...');
      const phoneWithCountry = `+91${phone}`;

      if (IS_DEV) {
        sessionStorage.setItem('signup_phone', phoneWithCountry);
        setLoading(false);
        navigate('/otp', {
          state: {
            phone: phoneWithCountry,
            isLogin: true,
            isMerchant
          }
        });
        return;
      }
      console.log('🔵 Phone with country code:', phoneWithCountry);
      console.log('🔵 Widget initialized?', widgetInitialized);

      // Ensure widget is initialized (it should be from useEffect, but double-check)
      if (true) {
        console.warn('⚠️ Widget not initialized yet, initializing now...');
        try {
          // await initMSG91Widget({
          //   phoneNumber: phoneWithCountry,
          //   onSuccess: (data) => {
          //     console.log('✅ Widget initialized successfully:', data);
          //     setWidgetInitialized(true);
          //   },
          //   onFailure: (error) => {
          //     console.error('❌ Widget init failed:', error);
          //     throw new Error('Failed to initialize OTP service');
          //   }
          // });
          // window.sendOtp(
          //   '919686713296', // mandatory
          //   (data) => console.log('OTP sent successfully: ', data),
          //   (error) => console.log('Error occurred: ', error)
          // );
          setWidgetInitialized(true);
        } catch (initError) {
          console.error('❌ Error initializing widget:', initError);
          throw new Error('OTP service is not ready. Please refresh the page and try again.');
        }
      }

      // Verify widget methods are available
      // if (typeof window.sendOTP !== 'function') {
      //   console.warn('⚠️ sendOTP method not available, waiting for methods...');
      //   // Wait for methods to be exposed (up to 3 seconds)
      //   let attempts = 0;
      //   while (typeof window.sendOTP !== 'function' && attempts < 15) {
      //     await new Promise(resolve => setTimeout(resolve, 200));
      //     attempts++;
      //   }
      //   if (typeof window.sendOTP !== 'function') {
      //     throw new Error('MSG91 widget methods not ready. Please refresh the page and try again.');
      //   }
      // }

      // Send OTP via MSG91 widget (widget should already be initialized)
      console.log('📞 Sending OTP via MSG91 widget...');
      console.log('🔍 Checking widget methods:', {
        sendOTP: typeof window.sendOTP,
        verifyOTP: typeof window.verifyOTP,
        resendOTP: typeof window.resendOTP
      });

      // Add a wrapper timeout to ensure we don't hang forever
      const sendOTPWithTimeout = Promise.race([
        sendOTP(phoneWithCountry),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('OTP request timed out after 15 seconds')), 15000)
        )
      ]);

      window.sendOtp(
        phoneWithCountry, // mandatory
        (sendResult) => {
          const isSuccess = sendResult?.success ||
            sendResult?.type === 'success' ||
            sendResult?.data?.type === 'success' ||
            sendResult?.callbackNotFired; // Fallback case

          if (isSuccess) {
            // Store phone in sessionStorage
            sessionStorage.setItem('signup_phone', phoneWithCountry);

            // Reset loading before navigation (good practice)
            setLoading(false);

            // Navigate to OTP verification
            console.log('🚀 Navigating to OTP verification page...');
            console.log('🚀 Navigation state:', { phone: phoneWithCountry, isLogin: true, isMerchant });

            // Navigate immediately
            navigate('/otp', {
              state: {
                phone: phoneWithCountry,
                isLogin: true,
                isMerchant
              }
            });
          } else {
            console.error('❌ OTP send response did not indicate success:', sendResult);
            throw new Error('OTP send response did not indicate success');
          }
        },
        (error) => {
          console.error('❌ Send OTP error:', error);
          setError(
            error.message || 'Failed to send OTP. Please try again.'
          );
          setLoading(false);
        }
      );

      // const sendResult = await sendOTPWithTimeout;
      // console.log('✅ OTP sent successfully:', sendResult);
      // console.log('🔍 Send result details:', {
      //   success: sendResult?.success,
      //   type: sendResult?.type,
      //   dataType: sendResult?.data?.type,
      //   callbackNotFired: sendResult?.callbackNotFired
      // });

      // // Check if response indicates success (multiple formats)
      // const isSuccess = sendResult?.success ||
      //   sendResult?.type === 'success' ||
      //   sendResult?.data?.type === 'success' ||
      //   sendResult?.callbackNotFired; // Fallback case

      // if (isSuccess) {
      //   // Store phone in sessionStorage
      //   sessionStorage.setItem('signup_phone', phoneWithCountry);

      //   // Reset loading before navigation (good practice)
      //   setLoading(false);

      //   // Navigate to OTP verification
      //   console.log('🚀 Navigating to OTP verification page...');
      //   console.log('🚀 Navigation state:', { phone: phoneWithCountry, isLogin: true, isMerchant });

      //   // Navigate immediately
      //   navigate('/otp', {
      //     state: {
      //       phone: phoneWithCountry,
      //       isLogin: true,
      //       isMerchant
      //     }
      //   });
      // } else {
      //   console.error('❌ OTP send response did not indicate success:', sendResult);
      //   throw new Error('OTP send response did not indicate success');
      // }
    } catch (error) {
      console.error('❌ Send OTP error:', error);
      setError(
        error.message || 'Failed to send OTP. Please try again.'
      );
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-md mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Link to="/">
            <LocuLogo showText={true} textSize="text-2xl" textColor="text-gray-900" />
          </Link>
        </div>

        {/* Back Button */}
        <button
          onClick={() => navigate(location.state?.from || '/')}
          className="mb-8 flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back
        </button>

        {/* Title */}
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {useEmailLogin ? 'Merchant Login' : 'Sign up or log in'}
        </h1>
        {isMerchant && (
          <p className="text-gray-600 mb-6">
            Join LOCU as a merchant and start listing your business
          </p>
        )}


        {useEmailLogin ? (
          /* Email/Password Form */
          <form onSubmit={handleEmailLogin} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Business Email
              </label>
              <input
                id="email"
                type="email"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="you@restaurant.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                autoFocus
              />
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-4 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Logging in...' : 'Log In'}
            </button>

            {/* Sign up link */}
            <p className="text-center text-sm text-gray-600">
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => navigate('/register', { state: { isMerchant } })}
                className="text-primary-600 hover:text-primary-700 font-semibold"
              >
                Sign up
              </button>
            </p>
          </form>
        ) : (
          /* Phone Number Form */
          <form onSubmit={(e) => { console.log('🔵 Form onSubmit triggered'); handleSubmit(e); }} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {/* Phone Number Input */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <div className="flex">
                <div className="flex items-center px-3 border border-r-0 border-gray-300 rounded-l-lg bg-gray-50">
                  <span className="text-sm text-gray-700">🇮🇳 +91</span>
                </div>
                <input
                  id="phone"
                  type="tel"
                  inputMode="numeric"
                  required
                  maxLength="10"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Phone Number"
                  value={phone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setPhone(value);
                    setError('');
                  }}
                  autoFocus
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !phone}
              onClick={() => console.log('🔵 Submit button clicked, disabled:', loading || !phone)}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-4 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Sending OTP...
                </span>
              ) : (
                'Next'
              )}
            </button>
          </form>
        )}

        {/* Privacy Policy Text */}
        <p className="mt-6 text-xs text-gray-500 text-center">
          We'll call or text you to confirm your number. Standard message and data rates apply.{' '}
          <Link to="/privacy" className="text-primary-600 hover:text-primary-700 underline">
            Privacy Policy
          </Link>
          .
        </p>
      </div>
    </div>
  );
};

export default Login;
